import React, { useEffect, useRef, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import MessagingList from "./messagingList";
import Moment from "react-moment";
import LexicalEditor from "./LexicalEditor";
import { useDispatch, useSelector } from "react-redux";

import {
  getCurrentUser,
  getUserProfilePic,
  getUserProfilePicTwo,
  loadUserProfilePic,
  loadUserProfilePicTwo,
} from "../../app/store/auth";

import { uploadFileToBlob } from "../../services/blobUploadService";
import { getAlert } from "../../services/appAlerts";
import {
  addSupportAppMessage,
  getSupportAddedNewMsgStatus,
  getSupportAppMsgList,
  getSupportAppMsgReadStatus,
  getSupportMsgPopupDocSasUrls,
  loadSupportAppMsgList,
  supportAppNewMsgAdded,
  updateSupportMessageRead,
} from "../../app/store/support";

const formatFileSize = (sizeInBytes) => {
  if (!sizeInBytes && sizeInBytes !== 0) return "";
  if (sizeInBytes < 1024) return `${sizeInBytes} B`;
  if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(1)} KB`;
  return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getPlainText = (html = "") =>
  html
    .replace(/<style[^>]*>.*?<\/style>/gis, "")
    .replace(/<script[^>]*>.*?<\/script>/gis, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();

const MessagingPopUpMod = ({
  msgShowHide,
  handleMsgModalShowHide,
  message,
  handleSendMessage,
  handleSupportMessage,
  isInline,
}) => {
  const dispatch = useDispatch();
  const currentUser = getCurrentUser();

  const [messageList, setMessageList] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [content, setContent] = useState("");
  const [editorResetKey, setEditorResetKey] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [btnSendProcess, setBtnSendProcess] = useState(false);

  const fileInputRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Redux Selectors
  const userProfilePic = useSelector(getUserProfilePic);
  const userProfilePicTwo = useSelector(getUserProfilePicTwo);

  const appMsgListSelector = useSelector(getSupportAppMsgList);
  const supportAddedNewMsgStatusSelector = useSelector(getSupportAddedNewMsgStatus);
  const appMsgReadStatusSelector = useSelector(getSupportAppMsgReadStatus);
  const supportMsgPopupDocSasUrlsSelector = useSelector(getSupportMsgPopupDocSasUrls);

  // Profile pictures
  const [profilePicData, setProfilePicData] = useState(null);
  const [profilePicTwoData, setProfilePicTwoData] = useState(null);

  // Scroll to bottom on message list update
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messageList]);

  // -----------------------------
  // Load current user's picture
  // -----------------------------
  useEffect(() => {
    if (currentUser?.id) {
      dispatch(loadUserProfilePic(currentUser.id));
    }
  }, []);

  useEffect(() => {
    if (userProfilePic) {
      setProfilePicData(userProfilePic);
    }
  }, [userProfilePic]);

  useEffect(() => {
    if (userProfilePicTwo) {
      setProfilePicTwoData(userProfilePicTwo);
    }
  }, [userProfilePicTwo]);

  // ------------------------------------------------------------
  // Load message thread when modal opens or message.id changes
  // ------------------------------------------------------------
  useEffect(() => {
    if (!message?.id) return;
    setIsLoading(true);
    dispatch(loadSupportAppMsgList(message.id));
    setDocuments([]);
    setContent("");
    setEditorResetKey((key) => key + 1);
    setMessageList([]);
    setBtnSendProcess(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [message?.id, dispatch]);

  // ------------------------------------------------------------
  // Build message list from Redux store
  // ------------------------------------------------------------
  useEffect(() => {
    if (!appMsgListSelector) return;

    const msgList = appMsgListSelector.map((item) => {
      const isSender = item.fromId === parseInt(currentUser.id);
      const performer = `${item.fromName} ${item.fromLastName}`;

      if (!isSender) {
        dispatch(loadUserProfilePicTwo(item.fromId));
      }

      return {
        author: isSender ? "send" : "receive",
        data: item.message,
        performer,
        date: item.messageDate,
        documents: item.documents,
        id: item.id,
        readStatus: item.readStatus,
      };
    });

    setMessageList(msgList);
    setIsLoading(false);

    // Mark unread threads as read
    const unread = msgList.some((x) => x.readStatus !== true);

    if (unread && message?.id) {
      dispatch(updateSupportMessageRead(message.id, currentUser.id));
    }

  }, [appMsgListSelector]);

  // -----------------------------
  // Editor and file upload
  // -----------------------------
  const handleChange = useCallback((content) => setContent(content), []);

  const onFileChange = useCallback((event) => {
    const newDocs = Array.from(event.target.files).map((file) => ({
      name: file.name,
      type: file.type,
      size: file.size,
      file,
    }));
    setDocuments((prev) => [...prev, ...newDocs]);
    event.target.value = "";
  }, []);

  const handleFileRemove = (file) =>
    setDocuments((prev) => prev.filter((f) => f !== file));

  // -----------------------------
  // Drag & Drop
  // -----------------------------
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const dropped = Array.from(e.dataTransfer.files).map((file) => ({
      name: file.name,
      type: file.type,
      size: file.size,
      file,
    }));

    setDocuments((prev) => [...prev, ...dropped]);
  };

  // -----------------------------
  // Submit Message
  // -----------------------------
  const onSubmit = () => {
    if (btnSendProcess) return;

    if (!getPlainText(content) && documents.length === 0) {
      getAlert("", "Please write a message or attach a file before sending.", "warning");
      return;
    }

    setBtnSendProcess(true);

    const data = new FormData();
    data.append("message", content);
    data.append("performerId", currentUser.id);
    data.append("communication", JSON.stringify(message));
    data.append("upDocs", JSON.stringify(documents));
    data.append("files", JSON.stringify(documents.map((file) => {
      return {
        name: file.name,
        type: file.type,
        size: file.size,
      };
    })));

    dispatch(addSupportAppMessage(data));
  };

  // Refresh list after new message sent
  useEffect(() => {
    if (!supportAddedNewMsgStatusSelector?.state) return;

    dispatch(loadSupportAppMsgList(message.id));

    if (handleSupportMessage) {
      handleSupportMessage(content, supportAddedNewMsgStatusSelector.obj);
    }

    setContent("");
    setEditorResetKey((key) => key + 1);
    setBtnSendProcess(false);
    setDocuments([]);
  }, [supportAddedNewMsgStatusSelector]);

  useEffect(() => {
    const uploadAllDocuments = async () => {
      if (supportMsgPopupDocSasUrlsSelector?.length > 0) {
        try {
          await Promise.all(
            documents.map((file, key) =>
              uploadFileToBlob(
                file.file,
                supportMsgPopupDocSasUrlsSelector[key]?.uploadUrl,
                (progress) => {
                  console.log(`Upload progress: ${progress}%`);
                }
              )
            )
          );

          // Runs only after ALL uploads finish   
          console.log("All files uploaded successfully!");
          dispatch(supportAppNewMsgAdded({ sasUrls: null, msg: null }));
          setDocuments([]);
          setBtnSendProcess(false);
        } catch (error) {
          console.error("Upload failed:", error);
          setBtnSendProcess(false);
        }
      }
    };

    uploadAllDocuments();
  }, [supportMsgPopupDocSasUrlsSelector]);

  const renderCoreContent = () => {
    return (
      <div className="wf-message-layout row m-0" style={{ height: isInline ? "auto" : "75vh" }}>
        {/* LEFT COLUMN: REPLY SECTION (40%) */}
        <div className="col-md-5 wf-reply-panel d-flex flex-column" style={{ minHeight: isInline ? "auto" : "450px", height: "auto", padding: isInline ? "14px !important" : "18px !important" }}>
          <div className="wf-section-title">
            <i className="fa fa-reply mr-2 text-primary" aria-hidden="true" />
            Write a Reply
          </div>

          {/* DROP ZONE */}
          <div
            className={`drop-zone ${isDragging ? "dragging" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <i className="fa fa-cloud-upload" aria-hidden="true"></i>
            <p>Drag & drop files here</p>
            <span>or click to browse</span>
          </div>

          {/* FILE PREVIEW GRID */}
          {documents.length > 0 && (
            <div className="file-preview-grid">
              {documents.map((file, index) => (
                <div key={`${file.name}-${index}`} className="file-card">
                  <div className="file-icon-wrapper">
                    <i
                      className={`fa ${file.type?.includes("image")
                        ? "fa-file-image-o"
                        : file.type?.includes("pdf")
                          ? "fa-file-pdf-o"
                          : "fa-file-text-o"
                        }`}
                      aria-hidden="true"
                    ></i>
                  </div>
                  <div className="file-content">
                    <div className="file-title" title={file.name}>
                      {file.name}
                    </div>
                    <div className="file-meta">
                      <span>{formatFileSize(file.size)}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-remove-file"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFileRemove(file);
                    }}
                    aria-label={`Remove ${file.name}`}
                  >
                    <i className="fa fa-times" aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* EDITOR */}
          <div className="wf-editor-shell flex-grow-1">
            <LexicalEditor
              key={editorResetKey}
              value={content}
              onChange={handleChange}
            />
          </div>

          {/* FOOTER ACTIONS */}
          <div className="wf-popup-footer mt-auto">
            <button
              type="button"
              className="wf-btn wf-btn-secondary"
              onClick={() => {
                if (handleMsgModalShowHide) handleMsgModalShowHide("");
              }}
            >
              <i className="fa fa-arrow-left" aria-hidden="true" />
              Back
            </button>
            <button
              type="button"
              className="wf-btn wf-btn-primary"
              onClick={onSubmit}
              disabled={btnSendProcess}
              style={{ marginLeft: "auto" }}
            >
              <i
                className={`fa ${btnSendProcess ? "fa-circle-o-notch fa-spin" : "fa-paper-plane"}`}
                aria-hidden="true"
              />
              {btnSendProcess ? "Sending..." : "Send Reply"}
            </button>
          </div>
        </div>

        {/* RIGHT COLUMN: MESSAGE LIST (60%) */}
        <div className="col-md-7 p-0 d-flex flex-column bg-white wf-message-thread-column" style={{ minHeight: isInline ? "auto" : "450px", height: isInline ? "auto" : "100%" }}>
          <div className="wf-chat-container flex-grow-1" ref={chatContainerRef} style={{ marginBottom: 0, overflowY: "auto", height: isInline ? "auto" : "100%", maxHeight: isInline ? "550px" : "none" }}>
            {isLoading ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", minHeight: "248px", color: "#94a3b8" }}>
                <i className="fa fa-spinner fa-spin" style={{ fontSize: "32px", marginBottom: "8px", color: "var(--primary-color, #2563eb)" }} aria-hidden="true" />
                <span style={{ fontSize: "13px", fontWeight: "600" }}>Loading messages...</span>
              </div>
            ) : messageList.length === 0 ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", minHeight: "248px", color: "#94a3b8" }}>
                <i className="fa fa-comments-o" style={{ fontSize: "32px", marginBottom: "8px", color: "var(--primary-color, #cbd5e1)" }} aria-hidden="true" />
                <span style={{ fontSize: "13px", fontWeight: "600" }}>No conversation messages yet. Write a reply below to start the discussion.</span>
              </div>
            ) : (
              messageList.map((item, index) => {
                const prevItem = index > 0 ? messageList[index - 1] : null;
                const showDateSeparator = !prevItem ||
                  new Date(item.date).toDateString() !== new Date(prevItem.date).toDateString();

                return (
                  <React.Fragment key={item.id}>
                    {showDateSeparator && (
                      <div className="wf-chat-date-separator">
                        <span>
                          <Moment format="MMMM D, YYYY">{item.date}</Moment>
                        </span>
                      </div>
                    )}
                    <MessagingList
                      message={item}
                      profilePicData={profilePicData}
                      profilePicTwoData={profilePicTwoData}
                    />
                  </React.Fragment>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  };

  if (isInline) {
    return (
      <div className="modern-popup-container" style={{ border: "1px solid var(--border-color)", borderRadius: "var(--radius-md)", overflow: "hidden" }}>
        <style>{`
          :root {
            --primary-color: #2563eb;
            --primary-hover: #1d4ed8;
            --primary-soft: #eff6ff;
            --secondary-color: #64748b;
            --secondary-hover: #475569;
            --border-color: #e2e8f0;
            --bg-light: #f8fafc;
            --text-main: #1e293b;
            --text-muted: #64748b;
            --radius-md: 0;
            --radius-sm: 0;
            --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
            --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1);
          }

          .modern-popup-container {
            height: auto;
            min-height: 0;
            padding: 0;
            overflow: visible;
            color: var(--text-main);
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          }
          .wf-message-layout {
            min-height: 0;
            overflow: visible;
          }
          .wf-message-thread-column {
            min-height: 0;
            overflow: visible;
          }

          /* ── Modern Chat Thread Container ── */
          .wf-chat-container {
            background: #ffffff;
            padding: 12px 20px;
            min-height: 0;
            overflow-y: auto !important;
            scrollbar-width: thin;
            scrollbar-color: #cbd5e1 transparent;
            display: flex;
            flex-direction: column;
            gap: 14px;
          }
          .wf-chat-container::-webkit-scrollbar {
            width: 6px;
          }
          .wf-chat-container::-webkit-scrollbar-track {
            background: transparent;
          }
          .wf-chat-container::-webkit-scrollbar-thumb {
            background-color: #cbd5e1;
            border-radius: 0;
          }

          /* ── Modern Chat Message Item ── */
          .wf-chat-item {
            display: flex;
            gap: 10px;
            max-width: 92%;
            align-items: flex-end;
            animation: rtMsgTabFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) both;
          }
          .wf-chat-item.is-sent {
            margin-left: auto;
            flex-direction: row-reverse;
          }
          .wf-chat-item.is-received {
            margin-right: auto;
          }

          /* Avatar styling */
          .wf-chat-avatar {
            width: 36px !important;
            height: 36px !important;
            border-radius: 0 !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            font-size: 12.5px !important;
            font-weight: 800 !important;
            flex-shrink: 0 !important;
            box-shadow: 0 3px 8px rgba(15, 23, 42, 0.08) !important;
            border: 1.5px solid #ffffff !important;
            background: var(--bg-light) !important;
            letter-spacing: 0.02em !important;
          }
          .wf-chat-item.is-sent .wf-chat-avatar {
            background: linear-gradient(135deg, rgba(255, 255, 255, 0.18) 0%, rgba(0, 0, 0, 0.06) 100%), var(--primary-color, #2563eb) !important;
            color: #ffffff !important;
            border: 1.5px solid #ffffff !important;
          }
          .wf-chat-item.is-received .wf-chat-avatar {
            background: linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%) !important;
            color: #475569 !important;
            border: 1.5px solid #cbd5e1 !important;
            box-shadow: 0 3px 8px rgba(15, 23, 42, 0.04) !important;
          }

          /* Bubble styling */
          .wf-chat-bubble {
            display: flex;
            flex-direction: column;
            gap: 5px;
            min-width: 140px;
          }
          .wf-chat-bubble-content {
            padding: 10px 14px;
            font-size: 13px;
            line-height: 1.45;
            word-break: break-word;
            box-shadow: 0 2px 4px rgb(0 0 0 / 0.02);
          }
          .wf-chat-item.is-sent .wf-chat-bubble-content {
            background: var(--primary-soft);
            border-radius: 0;
            border: 1px solid rgba(37, 99, 235, 0.15);
          }
          .wf-chat-item.is-sent .wf-chat-bubble-content,
          .wf-chat-item.is-sent .wf-chat-bubble-content *:not(a) {
            color: #1e293b;
          }
          .wf-chat-item.is-sent .wf-chat-bubble-content a {
            color: var(--primary-color);
            text-decoration: underline;
            font-weight: 500;
          }
          .wf-chat-item.is-received .wf-chat-bubble-content {
            background: #f1f5f9;
            border-radius: 0;
            border: 1px solid #e2e8f0;
          }
          .wf-chat-item.is-received .wf-chat-bubble-content,
          .wf-chat-item.is-received .wf-chat-bubble-content *:not(a) {
            color: #1e293b;
          }
          .wf-chat-item.is-received .wf-chat-bubble-content a {
            color: var(--primary-color);
            text-decoration: underline;
            font-weight: 500;
          }

          /* Meta / Sender Name / Time styling */
          .wf-chat-meta {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 11px;
            color: #94a3b8;
            margin-bottom: 2px;
            padding: 0 4px;
          }
          .wf-chat-item.is-sent .wf-chat-meta {
            justify-content: flex-end;
          }
          .wf-chat-sender {
            font-weight: 600;
            color: #64748b;
          }
          .wf-chat-time {
            font-weight: 500;
          }

          /* Attachment styling */
          .wf-chat-attachments {
            display: flex;
            flex-direction: column;
            gap: 5px;
            margin-top: 8px;
            padding: 0;
            list-style: none;
          }
          .wf-attachment-item {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 7px 10px;
            border-radius: 0;
            font-size: 12px;
            font-weight: 500;
            transition: all 0.2s ease;
            width: 100%;
            box-sizing: border-box;
          }
          .wf-chat-item.is-sent .wf-attachment-item {
            background: #ffffff;
            color: #475569;
            border: 1px solid rgba(37, 99, 235, 0.2);
            box-shadow: 0 1px 2px rgb(0 0 0 / 0.02);
          }
          .wf-chat-item.is-sent .wf-attachment-item:hover {
            border-color: var(--primary-color);
            color: #0f172a;
          }
          .wf-chat-item.is-received .wf-attachment-item {
            background: #ffffff;
            color: #475569;
            border: 1px solid #e2e8f0;
            box-shadow: 0 1px 2px rgb(0 0 0 / 0.02);
          }
          .wf-chat-item.is-received .wf-attachment-item:hover {
            border-color: #cbd5e1;
            color: #0f172a;
          }
          .wf-attachment-item button {
            background: none;
            border: none;
            padding: 0;
            color: inherit;
            font-weight: inherit;
            font-size: inherit;
            text-align: left;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            width: 100%;
            cursor: pointer;
            outline: none;
          }
          .wf-attachment-item button i {
            font-size: 14px;
            opacity: 0.8;
          }

          /* Date separator */
          .wf-chat-date-separator {
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 12px 0 8px 0;
            position: relative;
            width: 100%;
          }
          .wf-chat-date-separator::before {
            content: "";
            position: absolute;
            left: 0;
            right: 0;
            top: 50%;
            height: 1px;
            background: #f1f5f9;
            z-index: 1;
          }
          .wf-chat-date-separator span {
            background: #ffffff;
            padding: 4px 16px;
            font-size: 11px;
            font-weight: 600;
            color: #94a3b8;
            border-radius: 0;
            position: relative;
            z-index: 2;
            letter-spacing: 0.02em;
          }

          /* ── Editor Section Styling ── */
          .wf-reply-panel {
            background: #f8fafc;
            border-left: 1px solid #f1f5f9;
            min-height: 0;
            overflow: visible;
            padding: 18px !important;
          }

          .wf-section-title {
            font-size: 14px;
            font-weight: 700;
            color: var(--text-main);
            margin-bottom: 12px;
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .wf-section-title i {
            color: var(--primary-color);
            font-size: 1rem;
          }

          /* ── Drop Zone Styling ── */
          .drop-zone {
            border: 1.5px dashed #cbd5e1;
            border-radius: 0;
            background: #ffffff;
            transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 82px;
            cursor: pointer;
            margin-bottom: 14px;
            padding: 14px;
          }

          .drop-zone:hover, .drop-zone.dragging {
            border-color: var(--primary-color);
            background: var(--primary-soft);
            transform: translateY(-2px);
          }

          .drop-zone i {
            color: var(--primary-color);
            font-size: 1.4rem;
            margin-bottom: 5px;
            opacity: 0.8;
          }

          .drop-zone p {
            font-size: 13px;
            color: var(--text-main);
            font-weight: 600;
            margin: 0;
          }

          .drop-zone span {
            font-size: 12px;
            color: var(--text-muted);
            margin-top: 2px;
          }

          /* ── File Preview Grid ── */
          .file-preview-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 8px;
            margin-bottom: 12px;
            max-height: 112px;
            overflow-y: auto;
          }

          .file-card {
            background: #fff;
            border: 1px solid var(--border-color);
            border-radius: var(--radius-sm);
            padding: 8px 10px;
            display: flex;
            align-items: center;
            gap: 12px;
            box-shadow: var(--shadow-sm);
            transition: all 0.2s;
            position: relative;
          }

          .file-card:hover {
            border-color: var(--primary-color);
            box-shadow: var(--shadow-md);
            transform: translateY(-1px);
          }

          .file-icon-wrapper {
            width: 36px;
            height: 36px;
            background: var(--primary-soft);
            color: var(--primary-color);
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 0;
            font-size: 15px;
            flex-shrink: 0;
          }

          .file-content {
            flex: 1;
            min-width: 0;
          }

          .file-title {
            font-size: 12px;
            font-weight: 600;
            color: var(--text-main);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .file-meta {
            font-size: 11px;
            color: var(--text-muted);
            margin-top: 1px;
          }

          .btn-remove-file {
            width: 26px;
            height: 26px;
            border-radius: 0;
            border: none;
            background: transparent;
            color: var(--text-muted);
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s;
            flex-shrink: 0;
          }

          .btn-remove-file:hover {
            background: #fee2e2;
            color: #dc2626;
          }

          /* ── Action Buttons ── */
          .wf-popup-footer {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 10px;
            margin-top: 12px;
            padding-top: 12px;
            border-top: 1px solid var(--border-color);
          }

          .wf-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 8px 14px;
            font-size: 13px;
            font-weight: 600;
            border-radius: var(--radius-sm);
            cursor: pointer;
            transition: all 0.2s;
            border: 1px solid transparent;
            min-width: 96px;
          }

          .wf-btn-primary {
            background: var(--primary-color);
            color: #ffffff;
          }

          .wf-btn-primary:hover:not(:disabled) {
            background: var(--primary-hover);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
          }

          .wf-btn-secondary {
            background: #fff;
            color: var(--secondary-color);
            border-color: var(--border-color);
          }

          .wf-btn-secondary:hover:not(:disabled) {
            background: var(--bg-light);
            color: var(--secondary-hover);
            border-color: var(--secondary-hover);
          }

          .wf-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .wf-btn i {
            font-size: 13px;
          }
          .wf-editor-shell {
            min-height: 0;
            margin-bottom: 12px;
          }
          .wf-reply-panel .lexical-editor-container {
            border-radius: 0;
          }
          .wf-reply-panel .toolbar {
            padding: 6px !important;
            gap: 3px !important;
          }
          .wf-reply-panel .toolbar-item {
            width: 28px !important;
            height: 28px !important;
          }
          .wf-reply-panel .toolbar-select {
            height: 28px !important;
            min-width: 104px !important;
            font-size: 12px !important;
          }
          .wf-reply-panel .divider {
            height: 20px !important;
            margin: 3px 5px !important;
          }
          .wf-reply-panel .editor-input {
            min-height: 104px !important;
            max-height: 150px !important;
            padding: 10px 12px !important;
            font-size: 13px !important;
          }
          .wf-reply-panel .editor-placeholder {
            top: 10px !important;
            left: 12px !important;
            font-size: 13px !important;
          }
          @media (max-height: 760px) {
            .wf-reply-panel {
              padding: 14px !important;
            }
            .drop-zone {
              min-height: 68px;
              margin-bottom: 10px;
            }
            .wf-reply-panel .editor-input {
              min-height: 82px !important;
              max-height: 116px !important;
            }
          }
          @media (max-width: 767.98px) {
            .wf-message-layout {
              overflow: visible;
            }
            .wf-message-thread-column,
            .wf-reply-panel {
              height: auto;
              min-height: 0;
            }
          }
        `}</style>
        {renderCoreContent()}
        <input
          type="file"
          ref={fileInputRef}
          onChange={onFileChange}
          multiple
          hidden
        />
      </div>
    );
  }

  return (
    <Modal show={msgShowHide} size="xl" centered className="modern-modal">
      <style>{`
        .modern-modal .modal-content {
          border-radius: 12px !important;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          overflow: hidden !important;
          background: #ffffff !important;
          max-height: calc(100vh - 60px) !important;
          display: flex !important;
          flex-direction: column !important;
        }
        .modern-modal .modal-header {
          position: relative !important;
          padding: 18px 24px !important;
          background: linear-gradient(90deg, #eff6ff 0%, rgba(255,255,255,0.98) 54%, #ffffff 100%) !important;
          border-bottom: 1px solid #edf2f7 !important;
        }
        .modern-modal .modal-header::before {
          content: "" !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          bottom: 0 !important;
          width: 4px !important;
          background: #2563eb !important;
        }
        .modern-modal .modal-title {
          font-size: 17px !important;
          font-weight: 800 !important;
          color: #0f172a !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 10px !important;
        }
        .modern-modal .modal-title i {
          color: #2563eb !important;
        }
        .modern-modal .modal-body {
          padding: 0 !important;
          overflow: visible !important;
        }

        :root {
          --primary-color: #2563eb;
          --primary-hover: #1d4ed8;
          --primary-soft: #eff6ff;
          --secondary-color: #64748b;
          --secondary-hover: #475569;
          --border-color: #e2e8f0;
          --bg-light: #f8fafc;
          --text-main: #1e293b;
          --text-muted: #64748b;
          --radius-md: 0;
          --radius-sm: 0;
          --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
          --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1);
        }

        .wf-message-layout {
          min-height: 0;
          overflow: visible;
        }
        .wf-message-thread-column {
          min-height: 0;
          overflow: visible;
        }

        /* ── Modern Chat Thread Container ── */
        .wf-chat-container {
          background: #ffffff;
          padding: 12px 20px;
          min-height: 0;
          overflow-y: auto !important;
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 transparent;
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .wf-chat-container::-webkit-scrollbar {
          width: 6px;
        }
        .wf-chat-container::-webkit-scrollbar-track {
          background: transparent;
        }
        .wf-chat-container::-webkit-scrollbar-thumb {
          background-color: #cbd5e1;
          border-radius: 0;
        }

        /* ── Modern Chat Message Item ── */
        .wf-chat-item {
          display: flex;
          gap: 10px;
          max-width: 92%;
          align-items: flex-end;
          animation: rtMsgTabFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .wf-chat-item.is-sent {
          margin-left: auto;
          flex-direction: row-reverse;
        }
        .wf-chat-item.is-received {
          margin-right: auto;
        }

        /* Avatar styling */
        .wf-chat-avatar {
          width: 36px !important;
          height: 36px !important;
          border-radius: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-size: 12.5px !important;
          font-weight: 800 !important;
          flex-shrink: 0 !important;
          box-shadow: 0 3px 8px rgba(15, 23, 42, 0.08) !important;
          border: 1.5px solid #ffffff !important;
          background: var(--bg-light) !important;
          letter-spacing: 0.02em !important;
        }
        .wf-chat-item.is-sent .wf-chat-avatar {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.18) 0%, rgba(0, 0, 0, 0.06) 100%), var(--primary-color, #2563eb) !important;
          color: #ffffff !important;
          border: 1.5px solid #ffffff !important;
        }
        .wf-chat-item.is-received .wf-chat-avatar {
          background: linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%) !important;
          color: #475569 !important;
          border: 1.5px solid #cbd5e1 !important;
          box-shadow: 0 3px 8px rgba(15, 23, 42, 0.04) !important;
        }

        /* Bubble styling */
        .wf-chat-bubble {
          display: flex;
          flex-direction: column;
          gap: 5px;
          min-width: 140px;
        }
        .wf-chat-bubble-content {
          padding: 10px 14px;
          font-size: 13px;
          line-height: 1.45;
          word-break: break-word;
          box-shadow: 0 2px 4px rgb(0 0 0 / 0.02);
        }
        .wf-chat-item.is-sent .wf-chat-bubble-content {
          background: var(--primary-soft);
          border-radius: 0;
          border: 1px solid rgba(37, 99, 235, 0.15);
        }
        .wf-chat-item.is-sent .wf-chat-bubble-content,
        .wf-chat-item.is-sent .wf-chat-bubble-content *:not(a) {
          color: #1e293b;
        }
        .wf-chat-item.is-sent .wf-chat-bubble-content a {
          color: var(--primary-color);
          text-decoration: underline;
          font-weight: 500;
        }
        .wf-chat-item.is-received .wf-chat-bubble-content {
          background: #f1f5f9;
          border-radius: 0;
          border: 1px solid #e2e8f0;
        }
        .wf-chat-item.is-received .wf-chat-bubble-content,
        .wf-chat-item.is-received .wf-chat-bubble-content *:not(a) {
          color: #1e293b;
        }
        .wf-chat-item.is-received .wf-chat-bubble-content a {
          color: var(--primary-color);
          text-decoration: underline;
          font-weight: 500;
        }

        /* Meta / Sender Name / Time styling */
        .wf-chat-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 11px;
          color: #94a3b8;
          margin-bottom: 2px;
          padding: 0 4px;
        }
        .wf-chat-item.is-sent .wf-chat-meta {
          justify-content: flex-end;
        }
        .wf-chat-sender {
          font-weight: 600;
          color: #64748b;
        }
        .wf-chat-time {
          font-weight: 500;
        }

        /* Attachment styling */
        .wf-chat-attachments {
          display: flex;
          flex-direction: column;
          gap: 5px;
          margin-top: 8px;
          padding: 0;
          list-style: none;
        }
        .wf-attachment-item {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 7px 10px;
          border-radius: 0;
          font-size: 12px;
          font-weight: 500;
          transition: all 0.2s ease;
          width: 100%;
          box-sizing: border-box;
        }
        .wf-chat-item.is-sent .wf-attachment-item {
          background: #ffffff;
          color: #475569;
          border: 1px solid rgba(37, 99, 235, 0.2);
          box-shadow: 0 1px 2px rgb(0 0 0 / 0.02);
        }
        .wf-chat-item.is-sent .wf-attachment-item:hover {
          border-color: var(--primary-color);
          color: #0f172a;
        }
        .wf-chat-item.is-received .wf-attachment-item {
          background: #ffffff;
          color: #475569;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 2px rgb(0 0 0 / 0.02);
        }
        .wf-chat-item.is-received .wf-attachment-item:hover {
          border-color: #cbd5e1;
          color: #0f172a;
        }
        .wf-attachment-item button {
          background: none;
          border: none;
          padding: 0;
          color: inherit;
          font-weight: inherit;
          font-size: inherit;
          text-align: left;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          cursor: pointer;
          outline: none;
        }
        .wf-attachment-item button i {
          font-size: 14px;
          opacity: 0.8;
        }

        /* Date separator */
        .wf-chat-date-separator {
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 12px 0 8px 0;
          position: relative;
          width: 100%;
        }
        .wf-chat-date-separator::before {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          top: 50%;
          height: 1px;
          background: #f1f5f9;
          z-index: 1;
        }
        .wf-chat-date-separator span {
          background: #ffffff;
          padding: 4px 16px;
          font-size: 11px;
          font-weight: 600;
          color: #94a3b8;
          border-radius: 0;
          position: relative;
          z-index: 2;
          letter-spacing: 0.02em;
        }

        /* ── Editor Section Styling ── */
        .wf-reply-panel {
          background: #f8fafc;
          border-left: 1px solid #f1f5f9;
          min-height: 0;
          overflow: visible;
          padding: 18px !important;
        }

        .wf-section-title {
          font-size: 14px;
          font-weight: 700;
          color: var(--text-main);
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .wf-section-title i {
          color: var(--primary-color);
          font-size: 1rem;
        }

        /* ── Drop Zone Styling ── */
        .drop-zone {
          border: 1.5px dashed #cbd5e1;
          border-radius: 0;
          background: #ffffff;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 82px;
          cursor: pointer;
          margin-bottom: 14px;
          padding: 14px;
        }

        .drop-zone:hover, .drop-zone.dragging {
          border-color: var(--primary-color);
          background: var(--primary-soft);
          transform: translateY(-2px);
        }

        .drop-zone i {
          color: var(--primary-color);
          font-size: 1.4rem;
          margin-bottom: 5px;
          opacity: 0.8;
        }

        .drop-zone p {
          font-size: 13px;
          color: var(--text-main);
          font-weight: 600;
          margin: 0;
        }

        .drop-zone span {
          font-size: 12px;
          color: var(--text-muted);
          margin-top: 2px;
        }

        /* ── File Preview Grid ── */
        .file-preview-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 8px;
          margin-bottom: 12px;
          max-height: 112px;
          overflow-y: auto;
        }

        .file-card {
          background: #fff;
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          padding: 8px 10px;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: var(--shadow-sm);
          transition: all 0.2s;
          position: relative;
        }

        .file-card:hover {
          border-color: var(--primary-color);
          box-shadow: var(--shadow-md);
          transform: translateY(-1px);
        }

        .file-icon-wrapper {
          width: 36px;
          height: 36px;
          background: var(--primary-soft);
          color: var(--primary-color);
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 0;
          font-size: 15px;
          flex-shrink: 0;
        }

        .file-content {
          flex: 1;
          min-width: 0;
        }

        .file-title {
          font-size: 12px;
          font-weight: 600;
          color: var(--text-main);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .file-meta {
          font-size: 11px;
          color: var(--text-muted);
          margin-top: 1px;
        }

        .btn-remove-file {
          width: 26px;
          height: 26px;
          border-radius: 0;
          border: none;
          background: transparent;
          color: var(--text-muted);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .btn-remove-file:hover {
          background: #fee2e2;
          color: #dc2626;
        }

        /* ── Action Buttons ── */
        .wf-popup-footer {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid var(--border-color);
        }

        .wf-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 8px 14px;
          font-size: 13px;
          font-weight: 600;
          border-radius: var(--radius-sm);
          cursor: pointer;
          transition: all 0.2s;
          border: 1px solid transparent;
          min-width: 96px;
        }

        .wf-btn-primary {
          background: var(--primary-color);
          color: #ffffff;
        }

        .wf-btn-primary:hover:not(:disabled) {
          background: var(--primary-hover);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
        }

        .wf-btn-secondary {
          background: #fff;
          color: var(--secondary-color);
          border-color: var(--border-color);
        }

        .wf-btn-secondary:hover:not(:disabled) {
          background: var(--bg-light);
          color: var(--secondary-hover);
          border-color: var(--secondary-hover);
        }

        .wf-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .wf-btn i {
          font-size: 13px;
        }
        .wf-editor-shell {
          min-height: 0;
          margin-bottom: 12px;
        }
        .wf-reply-panel .lexical-editor-container {
          border-radius: 0;
        }
        .wf-reply-panel .toolbar {
          padding: 6px !important;
          gap: 3px !important;
        }
        .wf-reply-panel .toolbar-item {
          width: 28px !important;
          height: 28px !important;
        }
        .wf-reply-panel .toolbar-select {
          height: 28px !important;
          min-width: 104px !important;
          font-size: 12px !important;
        }
        .wf-reply-panel .divider {
          height: 20px !important;
          margin: 3px 5px !important;
        }
        .wf-reply-panel .editor-input {
          min-height: 104px !important;
          max-height: 150px !important;
          padding: 10px 12px !important;
          font-size: 13px !important;
        }
        .wf-reply-panel .editor-placeholder {
          top: 10px !important;
          left: 12px !important;
          font-size: 13px !important;
        }
        @media (max-height: 760px) {
          .wf-reply-panel {
            padding: 14px !important;
          }
          .drop-zone {
            min-height: 68px;
            margin-bottom: 10px;
          }
          .wf-reply-panel .editor-input {
            min-height: 82px !important;
            max-height: 116px !important;
          }
        }
        @media (max-width: 767.98px) {
          .wf-message-layout {
            overflow: visible;
          }
          .wf-message-thread-column,
          .wf-reply-panel {
            height: auto;
            min-height: 0;
          }
        }
      `}</style>
      <Modal.Header closeButton onHide={() => handleMsgModalShowHide("")}>
        <Modal.Title>
          <i className="fa fa-comments" aria-hidden="true"></i>
          Messages
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {renderCoreContent()}
      </Modal.Body>
      <input
        type="file"
        ref={fileInputRef}
        onChange={onFileChange}
        multiple
        hidden
      />
    </Modal>
  );
};

export default MessagingPopUpMod;
